import { type FC, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  LoadingSpinner,
  TabDialog,
  TabItem,
  TriggerButton,
  TriggerButtonIntent,
  useDialogWithPosition,
  VStack,
} from "widgets";

import { ActivityTypeEnum, NotificationTypeEnum } from "@/graphql.js";
import {
  type INotificationsState,
  loadNotificationsList,
  markAllAsRead,
  unwatchAllInfoChannels,
  watchAllInfoChannels,
} from "@/redux/modules/notifications.tsx";
import { type IAppState } from "@/redux/reducer.ts";
import { type ReduxDispatch } from "@/redux/redux-utils.ts";

import { FromNotification } from "../../../modules/notifications/Notification.js";
import style from "./styles/NotificationDialog.module.scss";

// all possible tab labels
enum TAB_OPTIONS {
  ALL = "all",
  COMMENTS = "comments",
  EVENTS = "events",
}

const Loading = () => (
  <div style={{ padding: "20px 10px 10px" }}>
    <LoadingSpinner />
  </div>
);

const NoNotifications = () => (
  <div style={{ padding: "20px 10px 10px", textAlign: "center", width: "100%" }}>You have no notifications.</div>
);

const NotificationDropDown: FC = () => {
  const connected = useSelector<IAppState>((state) => state.websocket.connected);
  const unread = useSelector<IAppState, string | undefined>((state) => {
    const count = state.notifications.notifications.filter((n) => !n.read).length;
    if (count === 0) return;
    return count > 99 ? "99+" : count.toString();
  });

  const { triggerRef, dialogRef, triggerProps, dialogProps, state, arrowProps } = useDialogWithPosition<
    HTMLDivElement,
    HTMLDivElement
  >({
    // offset: -5,
    onClose: () => (connected ? dispatch(markAllAsRead()) : undefined),
  });

  const dispatch = useDispatch<ReduxDispatch>();
  const { notifications, isLoading } = useSelector<IAppState, INotificationsState>((state) => state.notifications);

  const commentsNotifications = useMemo(
    () =>
      notifications.filter(
        (n) => n.type === NotificationTypeEnum.ProjectActivity && n.activity.type === ActivityTypeEnum.Comment,
      ),
    [notifications],
  );

  const eventNotifications = useMemo(
    () =>
      notifications.filter(
        (n) =>
          n.type === NotificationTypeEnum.Job ||
          n.type === NotificationTypeEnum.Workflow ||
          n.type === NotificationTypeEnum.Group,
      ),
    [notifications],
  );

  useEffect(() => {
    void dispatch(loadNotificationsList());
    dispatch(watchAllInfoChannels());

    return () => {
      dispatch(unwatchAllInfoChannels());
    };
  }, []);

  return (
    <>
      <TriggerButton
        {...triggerProps}
        icon={connected ? "notifications" : "notifications_off"}
        label={"Notifications"}
        ref={triggerRef}
        badge={connected ? unread : undefined}
        intent={state.isOpen ? TriggerButtonIntent.Active : undefined}
      />
      {!!state.isOpen && (
        <TabDialog
          ref={dialogRef}
          domProps={dialogProps}
          closeFn={() => state.close()}
          arrowProps={arrowProps}
          width={400}
        >
          <TabItem key={TAB_OPTIONS.ALL} title={"All Notifications"} icon={"notifications"}>
            {isLoading ? (
              <Loading />
            ) : notifications.length === 0 ? (
              <NoNotifications />
            ) : (
              <VStack className={style.list} spacing={7} style={{ paddingTop: 20 }}>
                {notifications.map((notification) => (
                  <FromNotification key={notification.id} notification={notification} />
                ))}
              </VStack>
            )}
          </TabItem>
          <TabItem key={TAB_OPTIONS.COMMENTS} title={"Comments"} icon={"chat_bubble"}>
            {isLoading ? (
              <Loading />
            ) : commentsNotifications.length === 0 ? (
              <NoNotifications />
            ) : (
              <VStack className={style.list} spacing={7} style={{ paddingTop: 20 }}>
                {commentsNotifications.map((notification) => (
                  <FromNotification key={notification.id} notification={notification} />
                ))}
              </VStack>
            )}
          </TabItem>
          <TabItem key={TAB_OPTIONS.EVENTS} title={"Events"} icon={"sync_alt"}>
            {isLoading ? (
              <Loading />
            ) : eventNotifications.length === 0 ? (
              <NoNotifications />
            ) : (
              <VStack className={style.list} spacing={7} style={{ paddingTop: 20 }}>
                {eventNotifications.map((notification) => (
                  <FromNotification key={notification.id} notification={notification} />
                ))}
              </VStack>
            )}
          </TabItem>
        </TabDialog>
      )}
    </>
  );
};

export { NotificationDropDown };
